import { z } from 'zod';

export type InstructionType = 'PRIZE_PAYOUT' | 'COMMISSION_PAYOUT' | 'REFUND_PAYOUT' | 'COLLECTION_INTENT' | 'EXIT_REFUND' | 'WAIVER_CREDIT';
export type Direction = 'OUTBOUND' | 'INBOUND';
export type InstructionStatus = 'PENDING' | 'APPROVED' | 'SENT_TO_RAIL' | 'RECONCILED' | 'FAILED' | 'CANCELLED';
export type Rail = 'NEFT' | 'IMPS' | 'UPI' | 'MANUAL' | 'CASH';

export const InstructionListItemSchema = z.object({
  id: z.string(),
  instructionType: z.string() as z.ZodType<InstructionType>,
  direction: z.string() as z.ZodType<Direction>,
  status: z.string() as z.ZodType<InstructionStatus>,
  amount: z.number(),
  beneficiaryName: z.string(),
  rail: z.string() as z.ZodType<Rail>,
  referenceType: z.string(),
  referenceId: z.string(),
  createdAt: z.string(),
});

export const InstructionDetailSchema = InstructionListItemSchema.extend({
  beneficiaryAccount: z.string(),
  beneficiaryIfsc: z.string().nullable(),
  beneficiaryUpi: z.string().nullable(),
  makerId: z.string(),
  checkerId: z.string().nullable(),
  approvedAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  reconciledAt: z.string().nullable(),
  bankRef: z.string().nullable(),
  failureReason: z.string().nullable(),
});

export const BankStatementRowSchema = z.object({
  date: z.string(),
  description: z.string(),
  debit: z.number().nullable(),
  credit: z.number().nullable(),
  balance: z.number().nullable(),
  reference: z.string().nullable(),
});

export const UnmatchedRowSchema = z.object({
  importId: z.string(),
  bankRow: BankStatementRowSchema,
});

export const ImportListItemSchema = z.object({
  id: z.string(),
  importDate: z.string().optional(),
  importedAt: z.string().optional(),
  fileRef: z.string(),
  totalRows: z.number().nullable().optional(),
  matchedRows: z.number().optional().default(0),
  unmatchedRows: z.number().optional().default(0),
  ignoredRows: z.number().nullable().optional(),
  status: z.enum(['PROCESSING', 'COMPLETED', 'FAILED']).or(z.string()),
});

/**
 * A row as the API actually returns it (`GET /staff/v1/reconciliation/imports/:id`), including
 * the G-86/G-87 resolution trail. `status` is the whole workflow:
 * `UNMATCHED` sits in the queue, `MATCHED` is reconciled against an instruction, `IGNORED` was
 * closed by a human with a reason.
 */
export const ReconciliationRowStatusSchema = z.enum(['UNMATCHED', 'MATCHED', 'IGNORED']);

export const ReconciliationRowSchema = z.object({
  id: z.string(),
  importId: z.string(),
  companyId: z.string(),
  rowDate: z.string(),
  description: z.string(),
  amount: z.number(),
  bankRef: z.string().nullable(),
  instructionId: z.string().nullable(),
  status: ReconciliationRowStatusSchema,
  resolutionNote: z.string().nullable().optional(),
  resolvedBy: z.string().nullable().optional(),
  resolvedAt: z.string().nullable().optional(),
  correctionNote: z.string().nullable().optional(),
  correctedBy: z.string().nullable().optional(),
  correctedAt: z.string().nullable().optional(),
});

export const ImportDetailSchema = ImportListItemSchema.extend({
  rows: z.array(ReconciliationRowSchema),
});

export type InstructionListItem = z.infer<typeof InstructionListItemSchema>;
export type InstructionDetail = z.infer<typeof InstructionDetailSchema>;
export type BankStatementRow = z.infer<typeof BankStatementRowSchema>;
export type UnmatchedRow = z.infer<typeof UnmatchedRowSchema>;
export type ImportListItem = z.infer<typeof ImportListItemSchema>;
export type ImportDetail = z.infer<typeof ImportDetailSchema>;
export type ReconciliationRow = z.infer<typeof ReconciliationRowSchema>;
export type ReconciliationRowStatus = z.infer<typeof ReconciliationRowStatusSchema>;

/**
 * Body of `POST /staff/v1/reconciliation/rows/:rowId/correct`. `reason` is mandatory — the API
 * rejects the mutation without it — and every field is optional because a correction fixes only
 * what the bank statement got wrong.
 */
export interface ReconciliationCorrectionPayload {
  reason: string;
  rowDate?: string;
  description?: string;
  amount?: number;
  bankRef?: string;
}
